import { Alert } from '@clay-run/glaze/alert';
import { Text } from '@clay-run/glaze/text';

import { gongStepBodyFormat, sortStepsByPosition, stepOrdinal } from '../../gong-flow-picker-api';
import type { LauncherChannel, SequenceStep } from '../../schemas';
import { sanitizeHtml } from '../SanitizedHtml';

/**
 * Launcher V2 — the before/after two-column diff (originally PR C QA
 * follow-up U12B/R16's AI-copy-generation review host).
 *
 * U7b (terra-l2ai-epic-076.12) STRIPPED this down to the one caller it has
 * left: `SequenceMappingDrawer.tsx`'s E2a reuse (the sequence-swap /
 * Gong-flow-rebind destructive confirm — "old sequence's steps" vs.
 * "candidate sequence's steps", by position, via {@link copyDiffRows}). The
 * generation-review host role moved to `GenerateWithAIDrawer.tsx` (the
 * drawer's own stage machine) plus the real per-step `StepReviewCard`
 * (U7a) rendered inline in the sequence's own step list — this component no
 * longer renders an Accept/Discard action row, a magic-wand button, or any
 * of the generation-specific copy ("Generated copy — review before it goes
 * into your draft", "Use generated copy", …). It is display-only now: two
 * columns, nothing to click.
 *
 * `copyDiffRows` and {@link TwoColumnBlock} are exported because
 * `SequenceMappingDrawer.tsx` (and this file's own tests) build directly on
 * them; `leftColumnLabel`/`rightColumnLabel`/`ariaLabel` are the only
 * overrides that caller still uses (its two `<CopyDiffView>` call sites pass
 * `leftColumnLabel`/`rightColumnLabel` — the old sequence's/candidate flow's
 * name — never `ariaLabel`).
 *
 * The two-column labels are the prototype's, verbatim ("Current subject" /
 * "Generated subject", "Current copy" / "Generated copy"), and so is its rule
 * of rendering ONLY the halves that actually changed (`copyDiffColumns`,
 * `:4802` — an unchanged subject row is noise between two things a rep must
 * compare).
 *
 * NOT carried over: the prototype's token-level highlighting
 * (`diffTokens`/`renderDiffOps`). This is a plain two-column before/after —
 * word-level diffing is a separate piece of work and is flagged for Spencer
 * rather than half-built here.
 */

/** One step's two sides, plus which halves are worth showing. */
export interface CopyDiffRow {
  position: number;
  /**
   * QA round 7, U5 (item 7) — THE step number this row's header shows
   * ("Step {ordinal}"), the generated/candidate side's 1-based index in the
   * POSITION-SORTED list — same convention as `SequenceStepEditor`'s header
   * and `DispatchInspector`. NOT always `position + 1`: `generatedSteps` is
   * not guaranteed contiguous from 0.
   */
  ordinal: number;
  headSubject: string;
  headBody: string;
  generatedSubject: string;
  generatedBody: string;
  subjectChanged: boolean;
  bodyChanged: boolean;
  /**
   * D1 (live-QA round 2, Batch 5) — whether THIS SIDE's body is real HTML,
   * decided per-step via `gongStepBodyFormat`'s hybrid rule (plan A2): that
   * side's own `gongStepDescriptor.step_type` (email-family is always rich)
   * combined with that side's own CURRENT body content (a non-email step is
   * promoted to rich only when its body is sniffed as Gong's real HTML
   * envelope) — see that function's own doc for the full rule and why `body`
   * specifically must be a live read, never cached. The two sides are
   * computed independently ON PURPOSE, from each side's own body string AND
   * its own descriptor, not shared — this component is reused for the
   * sequence-swap confirm diff (`SequenceMappingDrawer.tsx`'s E2a reuse),
   * where "Current" and "Generated"/candidate can be two DIFFERENT sequences
   * on two different channels.
   */
  headBodyFormat: 'plain' | 'rich';
  generatedBodyFormat: 'plain' | 'rich';
}

/**
 * Pairs the generated/candidate steps with the head's, by POSITION.
 *
 * Iterates the GENERATED/candidate steps because those are what a swap will
 * apply — a head step the candidate side didn't answer for is not going to
 * change, so it has no place in a review of what will change. The
 * missing-head fallback below is defense in depth for a head that moved
 * under the rep between opening the confirm and this render, not an
 * expected shape.
 *
 * `channels` gates each side's own `gongStepBodyFormat` call FIRST
 * (playbooks-inflection-wiring, 2026-09-17): a non-Gong step is plain
 * regardless of any `gongStepDescriptor` it might carry (`gongStepBodyFormat`
 * happened to return `'plain'` for an absent/unknown descriptor before this
 * change, which is why this was safe by accident rather than by statement —
 * see the body-format audit in that plan). Each side's channel is independent
 * on purpose: this function is reused for the sequence-swap confirm diff
 * where "Current" and "Generated"/candidate can be two different sequences on
 * two different channels.
 */
export function copyDiffRows(
  headSteps: readonly SequenceStep[],
  generatedSteps: readonly SequenceStep[],
  channels: { head: LauncherChannel; generated: LauncherChannel },
): CopyDiffRow[] {
  const headByPosition = new Map(headSteps.map((step) => [step.position, step]));
  const sortedGenerated = sortStepsByPosition(generatedSteps);
  return sortedGenerated.map((generated) => {
    const head = headByPosition.get(generated.position);
    const headSubject = head?.subject ?? '';
    const headBody = head?.body ?? '';
    const generatedSubject = generated.subject ?? '';
    const generatedBody = generated.body ?? '';
    const headBodyFormat = channels.head === 'gong_engage'
      ? gongStepBodyFormat(headBody, head?.gongStepDescriptor)
      : 'plain';
    const generatedBodyFormat = channels.generated === 'gong_engage'
      ? gongStepBodyFormat(generatedBody, generated.gongStepDescriptor)
      : 'plain';
    return {
      position: generated.position,
      ordinal: stepOrdinal(sortedGenerated, generated.position),
      headSubject,
      headBody,
      generatedSubject,
      generatedBody,
      subjectChanged: headSubject !== generatedSubject,
      // Codex adversarial-review finding 2 (Batch 5 fix-up) — raw string
      // equality alone misses a body-FORMAT-only change: during a
      // cross-channel/cross-sequence diff (e.g. the sequence-swap confirm,
      // which reuses this same function for two potentially different
      // sequences), the raw strings can be identical while one side renders
      // as parsed HTML and the other as literal text — a real, visible
      // difference a rep would otherwise never see flagged.
      bodyChanged: headBody !== generatedBody || headBodyFormat !== generatedBodyFormat,
      headBodyFormat,
      generatedBodyFormat,
    };
  });
}

const EMPTY = '(empty)';

/**
 * One side's body, plain text (whitespace-pre-wrap `Text`, unchanged) or
 * sanitized HTML (a plain div rendering `sanitizeHtml`'s output — Glaze's
 * `Text` renders a `<p>`/`<span>` and offers no `dangerouslySetInnerHTML`
 * passthrough, and this is a display-only render with no `contentEditable`
 * involved, so a plain host element is correct here — same posture as
 * `SignalsList.tsx`'s `sanitizeDescriptionHtml` render). `(empty)` still wins
 * for a blank body regardless of format: an HTML body that sanitizes down to
 * nothing is exactly as informative as a genuinely blank one — enforced by
 * checking the SANITIZED result's blankness (Codex adversarial-review
 * finding 3, Batch 5 fix-up), not the raw value, and reusing that same
 * sanitized string for the render below so the string is sanitized exactly
 * once.
 */
function OneColumnValue({ value, format }: { value: string; format: 'plain' | 'rich' }) {
  if (format === 'rich') {
    const sanitized = sanitizeHtml(value);
    if (sanitized.trim() === '') {
      return (
        <Text size="xs" className="whitespace-pre-wrap wrap-break-word">
          {EMPTY}
        </Text>
      );
    }
    return (
      <div
        className="text-xs whitespace-pre-wrap wrap-break-word"
        // Already sanitized via `sanitizeHtml` immediately above.
        dangerouslySetInnerHTML={{ __html: sanitized }}
      />
    );
  }
  if (value.trim() === '') {
    return (
      <Text size="xs" className="whitespace-pre-wrap wrap-break-word">
        {EMPTY}
      </Text>
    );
  }
  return (
    <Text size="xs" className="whitespace-pre-wrap wrap-break-word">
      {value}
    </Text>
  );
}

/**
 * One labelled before/after pair. Exported — this file's own tests build on
 * it directly, and it is the one piece of rendering `SequenceMappingDrawer`
 * needs (via `CopyDiffView` below) beyond `copyDiffRows`' plain data.
 */
export function TwoColumnBlock({
  leftLabel,
  rightLabel,
  left,
  right,
  leftFormat = 'plain',
  rightFormat = 'plain',
}: {
  leftLabel: string;
  rightLabel: string;
  left: string;
  right: string;
  /** Defaults to `'plain'`; the subject block never passes this (subjects are never HTML). */
  leftFormat?: 'plain' | 'rich';
  rightFormat?: 'plain' | 'rich';
}) {
  return (
    <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
      <div className="flex flex-col gap-1">
        <Text size="xs" variant="tertiary">
          {leftLabel}
        </Text>
        <OneColumnValue value={left} format={leftFormat} />
      </div>
      <div className="flex flex-col gap-1 rounded-md bg-bg-blue-light p-2">
        <Text size="xs" variant="tertiary">
          {rightLabel}
        </Text>
        <OneColumnValue value={right} format={rightFormat} />
      </div>
    </div>
  );
}

export interface CopyDiffViewProps {
  /** The "left"/current side's steps — the sequence's SAVED head in every real call site today. */
  headSteps: readonly SequenceStep[];
  /** The "right"/candidate side's steps — the swap/rebind candidate's steps. */
  generatedSteps: readonly SequenceStep[];
  /** The left side's channel — gates `copyDiffRows`' body-format decision (playbooks-inflection-wiring). */
  headChannel: LauncherChannel;
  /** The right side's channel — same gate, independently, for the right side. */
  generatedChannel: LauncherChannel;
  /**
   * `true` when the left side has moved since whatever produced `headSteps`
   * ran (a sibling save, an SSE-driven refetch). Non-blocking — the caller
   * still owns whatever action follows this display.
   */
  isHeadStale: boolean;
  /** `SequenceMappingDrawer`'s E2a reuse override — default: `'Current'`. */
  leftColumnLabel?: string;
  /** `SequenceMappingDrawer`'s E2a reuse override — default: `'Generated'`. */
  rightColumnLabel?: string;
  /** Default: `'Sequence copy comparison'`. */
  ariaLabel?: string;
}

export function CopyDiffView({
  headSteps,
  generatedSteps,
  headChannel,
  generatedChannel,
  isHeadStale,
  leftColumnLabel = 'Current',
  rightColumnLabel = 'Generated',
  ariaLabel = 'Sequence copy comparison',
}: CopyDiffViewProps) {
  const rows = copyDiffRows(headSteps, generatedSteps, { head: headChannel, generated: generatedChannel });

  return (
    <section
      aria-label={ariaLabel}
      className="flex flex-col gap-3 rounded-lg border border-border-action bg-bg-primary p-4"
    >
      {isHeadStale && (
        <Alert status="warning">
          This Sequence was saved by someone else since this comparison was generated. The{' '}
          {leftColumnLabel} column below is that newer copy.
        </Alert>
      )}
      {rows.map((row) => (
        <div
          key={row.position}
          className="flex flex-col gap-2 rounded-md border border-border-secondary p-3"
        >
          <Text size="xs" weight="medium">
            Step {row.ordinal}
          </Text>
          {row.subjectChanged && (
            <TwoColumnBlock
              leftLabel={`${leftColumnLabel} subject`}
              rightLabel={`${rightColumnLabel} subject`}
              left={row.headSubject}
              right={row.generatedSubject}
            />
          )}
          {row.bodyChanged && (
            <TwoColumnBlock
              leftLabel={`${leftColumnLabel} copy`}
              rightLabel={`${rightColumnLabel} copy`}
              left={row.headBody}
              right={row.generatedBody}
              leftFormat={row.headBodyFormat}
              rightFormat={row.generatedBodyFormat}
            />
          )}
          {!row.subjectChanged && !row.bodyChanged && (
            <Text size="xs" variant="tertiary">
              No change between {leftColumnLabel} and {rightColumnLabel} for this step.
            </Text>
          )}
        </div>
      ))}
    </section>
  );
}
